import { DataTable } from "@/components/datatable/components/DataTable";
import { tableSearchSchema, safeDecompress } from "@/components/datatable/hooks/useTableUrlState";
import { mapTableStateToQueryParams } from "@/components/datatable/utils";
import PageHeader from "@/components/common/PageHeader";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import useConfirm from "@/hooks/useConfirm";
import { preferencesKeys } from "@/query-keys";
import type { Preference } from "@/schemas/preferenceSchema";
import { services } from "@/services";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import usePreferencesQuery from "./_hooks/usePreferencesQuery";
import usePreferencesColumns from "./_hooks/useColumns";
import usePreferenceMutation from "./_hooks/usePreferenceMutation";
import CreatePreferenceForm from "./_components/CreatePreferenceForm";

export const Route = createFileRoute("/_private/preferences/")({
  beforeLoad: ({ context }) => {
    if (!context.auth.user?.isRoot) {
      throw redirect({ to: "/stats" });
    }
  },
  validateSearch: tableSearchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
  }),
  loader: async ({ deps, context: { queryClient } }) => {
    const tableState = safeDecompress(deps.q);
    const requestParams = mapTableStateToQueryParams(tableState);
    return queryClient.ensureQueryData({
      queryKey: preferencesKeys.lists(deps.q),
      queryFn: () => services.getPreferences(requestParams),
    });
  },
  component: RouteComponent,
  errorComponent: ({ error }) => <ErrorBoundary error={error} />,
});

function RouteComponent() {
  const search = Route.useSearch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loaderData = Route.useLoaderData();
  const { data: queryData } = usePreferencesQuery(search.q, loaderData);

  const existingTypes = (queryData?.items ?? [])
    .filter((p) => p.deletedAt === null)
    .map((p) => p.type);

  const columns = usePreferencesColumns();
  const { deletePreference, isDeleting } = usePreferenceMutation();

  const deleteConfirm = useConfirm({
    title: t("common.confirmDeleteTitle"),
    description: t("common.confirmDeleteDescription"),
    isPending: isDeleting,
  });

  const handleView = useCallback(
    (row: Preference) =>
      navigate({ to: "/preferences/$preferenceId", params: { preferenceId: row.id } }),
    [navigate],
  );

  const handleDelete = useCallback(
    (row: Preference) => {
      deleteConfirm.confirm(() => {
        deletePreference(row.id);
      });
    },
    [deleteConfirm, deletePreference],
  );

  return (
    <div className="flex-1 flex flex-col gap-6">
      <PageHeader
        title={t("preferences.list.title")}
        subtitle={t("preferences.list.subtitle")}
        action={<CreatePreferenceForm existingTypes={existingTypes} />}
      />

      <DataTable<Preference>
        searchPlaceholder={t("preferences.list.searchPlaceholder")}
        data={queryData?.items}
        nextCursor={queryData?.nextCursor}
        hasMore={queryData?.hasMore}
        columns={columns}
        queryKey={preferencesKeys.lists(search.q)}
        navigateFrom="/preferences"
        searchFrom="/_private/preferences/"
        showActions={true}
        onView={handleView}
        onDelete={handleDelete}
        searchable={true}
      />

      {deleteConfirm.confirmDialog}
    </div>
  );
}
